import { trace, SpanStatusCode, SpanKind, Tracer, Span } from '@opentelemetry/api';
import { apiClient } from './client';
import { createContextLogger } from '../utils/logger';

const logger = createContextLogger('TelemetryService');

interface TelemetryConfig {
  serviceName: string;
  serviceVersion: string;
  environment: string;
  endpoint?: string;
  enableConsoleExporter?: boolean;
  enableAutoInstrumentation?: boolean;
  sampleRate?: number;
}

// Extended config type with internal telemetry span
interface ConfigWithSpan extends Record<string, unknown> {
  _telemetrySpan?: Span;
}

export class TelemetryService {
  private tracer: Tracer | null = null;
  private isInitialized = false;
  private config: TelemetryConfig | null = null;

  /**
   * Initialize OpenTelemetry with configuration
   */
  initialize(config: TelemetryConfig): void {
    if (this.isInitialized) {
      logger.warn('Telemetry already initialized');
      return;
    }

    try {
      this.config = config;

      // For now, initialize a basic tracer
      // The auto-instrumentation can be added separately
      this.tracer = trace.getTracer(config.serviceName, config.serviceVersion);
      this.isInitialized = true;

      logger.info('OpenTelemetry initialized successfully', {
        serviceName: config.serviceName,
        environment: config.environment,
        hasEndpoint: !!config.endpoint,
      });

      // Integrate with existing API client
      this.setupApiClientIntegration();

    } catch (error) {
      logger.error('Failed to initialize OpenTelemetry:', error);
    }
  }

  /**
   * Integrate OpenTelemetry with existing API client
   */
  private setupApiClientIntegration(): void {
    // Add request interceptor for tracing
    apiClient.addRequestInterceptor((config, url) => {
      const span = this.tracer?.startSpan(`HTTP ${config.method || 'GET'}`, {
        kind: SpanKind.CLIENT,
        attributes: {
          'http.method': config.method || 'GET',
          'http.url': url,
          'http.user_agent': navigator.userAgent,
        },
      });

      // Store span in request metadata
      const configWithSpan = config as ConfigWithSpan;
      configWithSpan._telemetrySpan = span;
      
      return config;
    });

    // Add response interceptor for span completion
    apiClient.addResponseInterceptor((response, _url) => {
      const responseWithSpan = response as ConfigWithSpan;
      const span = responseWithSpan._telemetrySpan;
      
      if (span) {
        span.setAttributes({
          'http.status_code': response.status,
          'http.response.size': response.headers.get('content-length') || 0,
        });

        if (response.ok) {
          span.setStatus({ code: SpanStatusCode.OK });
        } else {
          span.setStatus({ 
            code: SpanStatusCode.ERROR, 
            message: response.statusText 
          });
        }

        span.end();
      }

      return response;
    });

    // Add error handler for failed requests
    apiClient.addErrorHandler((error) => {
      if (this.tracer) {
        const span = this.tracer.startSpan('API Error', {
          kind: SpanKind.CLIENT,
          attributes: {
            'error.type': error.constructor.name,
            'error.message': error.message,
            'error.status': error.status || 0,
          },
        });

        span.setStatus({ 
          code: SpanStatusCode.ERROR, 
          message: error.message 
        });
        span.end();
      }
    });
  }

  /**
   * Create a custom span
   */
  createSpan(name: string, attributes?: Record<string, string | number | boolean>): Span | null {
    if (!this.tracer) {
      // Don't log warning - telemetry might be intentionally disabled
      return null;
    }

    return this.tracer.startSpan(name, {
      attributes,
    });
  }

  /**
   * Track user interaction
   */
  trackUserInteraction(action: string, target: string, attributes?: Record<string, string | number | boolean>): void {
    if (!this.isInitialized) return;
    
    const span = this.createSpan(`User.${action}`, {
      'user.action': action,
      'user.target': target,
      'user.timestamp': Date.now(),
      ...attributes,
    });

    if (span) {
      span.setStatus({ code: SpanStatusCode.OK });
      span.end();
    }
  }

  /**
   * Track page navigation
   */
  trackNavigation(from: string, to: string, attributes?: Record<string, string | number | boolean>): void {
    if (!this.isInitialized) return;
    
    const span = this.createSpan('Navigation', {
      'navigation.from': from,
      'navigation.to': to,
      'navigation.timestamp': Date.now(),
      ...attributes,
    });

    if (span) {
      span.setStatus({ code: SpanStatusCode.OK });
      span.end();
    }
  }

  /**
   * Track custom business events
   */
  trackEvent(name: string, attributes?: Record<string, string | number | boolean>): void {
    if (!this.isInitialized) return;
    
    const span = this.createSpan(name, {
      'event.timestamp': Date.now(),
      ...attributes,
    });

    if (span) {
      span.setStatus({ code: SpanStatusCode.OK });
      span.end();
    }
  }

  /**
   * Track errors
   */
  trackError(error: Error, context?: string, attributes?: Record<string, string | number | boolean>): void {
    if (!this.isInitialized) return;
    
    const span = this.createSpan('Error', {
      'error.type': error.constructor.name,
      'error.message': error.message,
      'error.stack': error.stack,
      'error.context': context,
      'error.timestamp': Date.now(),
      ...attributes,
    });

    if (span) {
      span.setStatus({ 
        code: SpanStatusCode.ERROR, 
        message: error.message 
      });
      span.end();
    }
  }

  /**
   * Get current trace ID for correlation
   */
  getCurrentTraceId(): string | null {
    if (!this.isInitialized) return null;
    
    const activeSpan = trace.getActiveSpan();
    return activeSpan?.spanContext().traceId || null;
  }

  /**
   * Shutdown telemetry
   */
  async shutdown(): Promise<void> {
    if (this.isInitialized) {
      this.isInitialized = false;
      logger.info('OpenTelemetry shutdown completed');
    }
  }

  /**
   * Get configuration for debugging
   */
  getConfig(): TelemetryConfig | null {
    return this.config;
  }
}

// Export singleton instance
export const telemetryService = new TelemetryService(); 